import { z } from 'zod';

// Common validation schemas
export const emailSchema = z.string().email('Invalid email address');

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/\d/, 'Password must contain at least one number');

export const phoneSchema = z
  .string()
  .min(10, 'Phone number must be at least 10 digits')
  .regex(/^[\d\s\-+()]+$/, 'Invalid phone number format');

export const requiredString = (fieldName: string) =>
  z.string().min(1, `${fieldName} is required`);

// Login form schema
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Business registration schema
export const businessSchema = z.object({
  name: requiredString('Business name'),
  address: requiredString('Address'),
  city: requiredString('City'),
  state: requiredString('State'),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code'),
  phone: phoneSchema,
  industry: requiredString('Industry'),
});

// Owner registration schema
export const ownerSchema = z.object({
  firstName: requiredString('First name'),
  lastName: requiredString('Last name'),
  email: emailSchema,
  password: passwordSchema,
  phone: phoneSchema,
});

// Signup form schema
export const signupSchema = z.object({
  business: businessSchema,
  owner: ownerSchema,
});

export type SignupFormData = z.infer<typeof signupSchema>;

// User form schema
export const userSchema = z.object({
  email: emailSchema,
  firstName: requiredString('First name'),
  lastName: requiredString('Last name'),
  password: passwordSchema.optional(),
  phone: phoneSchema.optional().or(z.literal('')),
  role: z.enum(['admin', 'manager', 'customer_service', 'viewer']),
});

export type UserFormData = z.infer<typeof userSchema>;

// Medication form schema
export const medicationSchema = z.object({
  name: requiredString('Medication name'),
  description: requiredString('Description'),
  dosage: requiredString('Dosage'),
  form: requiredString('Form'),
  price: z.number().min(0, 'Price must be positive'),
});

export type MedicationFormData = z.infer<typeof medicationSchema>;

// Order form schema
export const orderSchema = z.object({
  patientId: requiredString('Patient'),
  medicationId: requiredString('Medication'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  notes: z.string().optional(),
});

export type OrderFormData = z.infer<typeof orderSchema>;

// Email template form schema
export const emailTemplateSchema = z.object({
  name: requiredString('Template name'),
  subject: requiredString('Subject'),
  body: requiredString('Body'),
});

export type EmailTemplateFormData = z.infer<typeof emailTemplateSchema>;



